import { Component, computed, EventEmitter, inject, input, Output, Signal } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Swiper } from 'swiper/types';
import { TransactionReceiptFile, TransactionReceiptStatus } from '../../../../../../_interfaces/transactions.interface';
import { imageFormats } from '../../_helpers/transactions-helpers';
import { ReceiptUploadsApiService } from '../../../../../../_services/receipt-uploads-api-service/receipt-uploads-api.service';
import { ToastService } from '../../../../../../_services/toast-service/toast.service';
import { FileDownloaderService } from '../../../../../../_services/file-downloader.service';
import { FileSizePipe } from '../../../../../../shared/pipes/file-size.pipe';
import {
  StatusBadgeComponent,
  statusBadgeDepartments,
} from '../../../../../../shared/components/status-badge/status-badge.component';
import { calcConditionalEnvBasedUrl } from '../../_helpers/localstack-url-helper';

@Component({
  selector: 'app-receipt-preview-box',
  imports: [NgOptimizedImage, FileSizePipe, StatusBadgeComponent],
  providers: [ReceiptUploadsApiService],
  templateUrl: './receipt-uploaded-type-display.component.html',
  styleUrl: '../../../../../../../static/styles/components/file-preview.scss',
})
export class ReceiptUploadedTypeDisplayComponent {
  private readonly receiptUploadsApiService = inject(ReceiptUploadsApiService);
  private readonly fileDownloader = inject(FileDownloaderService);
  private readonly http = inject(HttpClient);
  readonly toastService = inject(ToastService);

  protected readonly statusBadgeDepartments = statusBadgeDepartments;
  protected readonly TransactionReceiptStatus = TransactionReceiptStatus;

  hideDelete = input<boolean>(false);
  receipt = input.required<TransactionReceiptFile>();
  receiptIndex = input<number>(0); // Index of this receipt in the carousel
  isActive = input<boolean>(false); // Whether this receipt is currently active in the carousel
  swiperInstance = input<Swiper | undefined>(undefined); // Optional swiper instance for thumbnail navigation

  @Output() viewReceipt = new EventEmitter<TransactionReceiptFile>();
  @Output() receiptDeleted = new EventEmitter();

  fileTypeIcon: Signal<string | undefined> = computed<string | undefined>(() => {
    const filetype = this.receipt().type;
    if (!filetype) return '';

    if (filetype.includes('pdf')) {
      return './assets/icons/pdf-file-icon.svg';
    }
    if (imageFormats.some((ext) => filetype.includes(ext))) {
      return './assets/icons/img-file-icon.svg';
    } else {
      return '';
    }
  });

  onViewReceipt() {
    // If swiper instance is provided, navigate to this receipt's slide
    const swiper = this.swiperInstance();
    const index = this.receiptIndex();

    if (swiper) {
      swiper.slideTo(index);
    }

    // Still emit the event for backward compatibility
    this.viewReceipt.emit(this.receipt());
  }

  /**
   * Download the receipt file
   * Backend now provides direct S3 URLs in receipt.url
   */
  downloadReceipt() {
    // Fix LocalStack URL for local development
    const url = calcConditionalEnvBasedUrl(this.receipt().url);

    // Fetch the file from the S3 URL
    this.http.get(url, { responseType: 'blob' }).subscribe({
      next: (blob: Blob) => {
        const filename = this.receipt().name || `receipt-${this.receipt().id}`;
        this.fileDownloader.download(blob, filename);
      },
      error: () => {
        this.toastService.show('Failed to download receipt.', 'iconoir-xmark');
      },
    });
  }

  deleteReceipt() {
    return this.receiptUploadsApiService.deleteTransactionReceipt(this.receipt().id).subscribe({
      next: () => {
        this.receiptDeleted.emit(true);
        this.toastService.show('Receipt deleted successfully.', 'iconoir-check-circle');
      },
      error: () => {
        this.toastService.show('Something went wrong. Receipt could not be deleted.', 'iconoir-xmark');
      },
    });
  }
}
